import { createSlice, createAction } from '@reduxjs/toolkit';
import { get, omit, pick } from 'lodash';
import { PAGE_TYPES, PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import { searchResponseSchema } from 'store/schema/search';
import formatSearchFacilities from 'utils/store/formatSearchFacilities';
import { API } from 'store/middleware/api';
import { allowedSearchParams } from 'lib/search';
import { extractNmpKeyword } from 'config/nmpMetadata';
import { NEAR_ME_KEYWORD_TO_SIZE } from 'utils/filterCount';

import { PAGES } from 'store/pages/slice';

export const SEARCH = {
	REQUEST: 'SFDC/SEARCH/SEARCH_REQUEST',
	SUCCESS: 'SFDC/SEARCH/SEARCH_SUCCESS',
	FAILURE: 'SFDC/SEARCH/SEARCH_FAILURE',
	DEALS_REQUEST: 'SFDC/SEARCH/SEARCH_REQUEST',
	DEALS_SUCCESS: 'SFDC/SEARCH/SEARCH_SUCCESS',
	DEALS_FAILURE: 'SFDC/SEARCH/SEARCH_FAILURE',
	HIGHLIGHT_FACILITY: 'SFDC/SEARCH/HIGHLIGHT_FACILITY',
	PAGE_REQUEST: 'SFDC/SEARCH/SEARCH_PAGE_REQUEST',
	PAGE_SUCCESS: 'SFDC/SEARCH/SEARCH_PAGE_SUCCESS',
	PAGE_FAILURE: 'SFDC/SEARCH/SEARCH_PAGE_FAILURE',
	SEARCH_LANDING_REQUEST: 'SFDC/SEARCH/SEARCH_LANDING_REQUEST',
	SEARCH_LANDING_SUCCESS: 'SFDC/SEARCH/SEARCH_LANDING_SUCCESS',
	SEARCH_LANDING_FAILURE: 'SFDC/SEARCH/SEARCH_LANDING_FAILURE',
	STORAGE_DEALS_REQUEST: 'SFDC/PAGES/STORAGE_DEALS_REQUEST',
	STORAGE_DEALS_SUCCESS: 'SFDC/PAGES/STORAGE_DEALS_SUCCESS',
	STORAGE_DEALS_FAILURE: 'SFDC/PAGES/STORAGE_DEALS_FAILURE',
	MAP_REDO_SEARCH: 'SFDC/SEARCH/MAP_REDO_SEARCH',
};

export const initialState = {
	loading: false,
	error: false,
	errorMessage: null,
	highlightedFacility: null,
	page: 1,
	numPages: 0,
	totalFacilities: 0,
	lastInOriginalSearch: null,
	totalUnits: 0,
	facilitiesPerPage: 15,
	facilities: [],
	landingFacilities: [],
	landingLocation: {},
	irmMarket: null,
	headlines: {},
	isBellhopsMarket: false,
	staticMapUrl: null,
	location: {},
	polygons: {},
	phone: {},
	meta: {},
	url: {},
	searchType: null,
	content: {},
	drilldownLinks: null,
	cityDrilldownLinks: null,
	searchId: null,
	searchParams: {
		location: null,
		searchType: null,
		moveInDate: null,
		sqft: null,
		order: 'recommended',
	},
	mapRedoSearch: false,
	promoCounts: {},
	facilityBadges: [],
};

const SEARCH_SUCCESS_TYPES = [
	SEARCH.SUCCESS,
	SEARCH.SEARCH_LANDING_SUCCESS,
	SEARCH.STORAGE_DEALS_SUCCESS,
	SEARCH.PAGE_SUCCESS,
];

const PSP_SUCCESS_TYPES = [
	PAGES.CAR_PSP_PAGE_SUCCESS,
	PAGES.STORAGE_PAGE_SUCCESS,
	PAGES.BOAT_PSP_PAGE_SUCCESS,
	PAGES.RV_PSP_PAGE_SUCCESS,
];

const searchSlice = createSlice({
	name: 'search',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(SEARCH.REQUEST, (state) => ({
				...state,
				loading: true,
			}))
			.addCase(SEARCH.FAILURE, (state) => ({
				...state,
				loading: false,
			}))
			.addCase(SEARCH.HIGHLIGHT_FACILITY, (state, { payload }) => ({
				...state,
				highlightedFacility: payload.facilityId,
			}))
			.addCase(SEARCH.MAP_REDO_SEARCH, (state, { payload }) => ({
				...state,
				mapRedoSearch: payload.mapRedoSearch,
			}))
			.addMatcher(
				(action) => SEARCH_SUCCESS_TYPES.includes(action.type),
				(state, { payload }) => {
					const { entities, result } = payload;
					const search = entities.search[result.search];

					if (state?.content?.tpReviews) {
						result.content.tpReviews = state.content.tpReviews;
					}

					return {
						...state,
						canonicalUrl: result.seo && result.seo.canonical,
						canonicalWithQueryParams:
							result.seo && result.seo.canonicalWithQueryParams,
						prevUrl: result.seo && result.seo.prev,
						nextUrl: result.seo && result.seo.next,
						meta: result.meta,
						url: search.url,
						loading: false,
						facilitiesPerPage: search.facilitiesPerPage,
						content: result.content
							? omit(result.content, [
									'housingMetrics',
									'drillDownLinks.zips',
									'drillDownLinks.sizes',
							  ])
							: {},
						drilldownLinks: result.content.drillDownLinks,
						location: search.location,
						polygons: search.polygons,
						irmMarket: search.irmMarket,
						headlines: search.headlines,
						facetCounts: search.facets,
						isBellhopsMarket: search.isBellhopsMarket,
						page: search.page,
						numPages: search.numPages,
						staticMapUrl: search.staticMapUrl,
						totalFacilities: search.totalFacilities,
						lastInOriginalSearch: search.lastInOriginalSearch,
						totalUnits: search.totalUnits,
						facilities: search.facilities || [],
						badgedFacilities: search.badgedFacilities || {},
						phone: result.phone,
						localPhone: state.localPhone || search.localPhone || {},
						searchType: search.searchType,
						searchParams: {
							...search.searchParams,
							searchType: search.searchType,
						},
						searchId: search.searchId,
						promoCounts: search.promoCounts,
						highlightedFacility: state.highlightedFacility,
					};
				}
			)
			.addMatcher(
				(action) => PSP_SUCCESS_TYPES.includes(action.type),
				(state, { payload }) => {
					const search =
						payload.entities.search[payload.result.search];
					return {
						...state,
						landingFacilities: (search && search.facilities) || [],
						landingLocation: search && search.location,
						searchId: search && search.searchId,
					};
				}
			);
	},
});

// Synchronous action creators using createAction for backward compatibility
export const highlightFacility = createAction(
	SEARCH.HIGHLIGHT_FACILITY,
	(facilityId) => ({ payload: { facilityId } })
);
export const clearHighlightedFacility = createAction(
	SEARCH.HIGHLIGHT_FACILITY,
	() => ({ payload: { facilityId: null } })
);

export const mapRedoSearchAction = createAction(
	SEARCH.MAP_REDO_SEARCH,
	(mapRedoSearch) => ({ payload: { mapRedoSearch } })
);

// CALL_API action creators (use middleware, cannot be slice reducers)
export function getSearchPage(query) {
	const params = pick(query, allowedSearchParams);

	params.listingsPerPage = 30;

	return {
		[API]: {
			types: [
				SEARCH.PAGE_REQUEST,
				SEARCH.PAGE_SUCCESS,
				SEARCH.PAGE_FAILURE,
			],
			endpoint: '/page/search',
			adapter: formatSearchFacilities,
			schema: searchResponseSchema,
			query: {
				...params,
			},
		},
	};
}

export function getSearchLandingPage(query, meta = {}) {
	const params = pick(query, allowedSearchParams);
	params.listingsPerPage = 30;

	return {
		[API]: {
			types: [
				SEARCH.SEARCH_LANDING_REQUEST,
				SEARCH.SEARCH_LANDING_SUCCESS,
				SEARCH.SEARCH_LANDING_FAILURE,
			],
			endpoint: '/page/searchLanding',
			adapter: formatSearchFacilities,
			schema: searchResponseSchema,
			meta,
			query: {
				...params,
			},
		},
	};
}

export function getCityLandingPage(params, query) {
	return getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			subPageType: 'cityLandingPage',
			...query,
		},
		{ pageType: PAGE_TYPES.CITY_LANDING }
	);
}

const NEAR_ME_KEYWORD_TO_AMENITY = {
	'wine-storage': 'wine-storage',
	'climate-controlled-storage': 'climate-controlled',
	'24-hour-storage': '24-hour-access',
	'drive-up-storage': 'drive-up-storage-units',
	'storage-with-electricity': 'storage-with-electricity',
	'storage-with-free-truck-rental': 'free-truck-rental',
};

const NEAR_ME_KEYWORD_TO_VEHICLE = {
	'car-storage': 'car',
	'boat-storage': 'boat',
	'rv-storage': 'rv',
};

export function getNearMePages(params, query) {
	return (dispatch, getState) => {
		const { user } = getState();
		const geolocation = get(user, 'geolocation', {});
		const city = geolocation.city || params.city || '';
		const state = geolocation.regionCode || params.state || '';
		const location = [city, state].filter(Boolean).join(', ');

		const nmpKeyword = extractNmpKeyword(params.keyword);
		const nmpSlug = params.keyword || '';
		const amenity = NEAR_ME_KEYWORD_TO_AMENITY[params.keyword];
		const vehicle = NEAR_ME_KEYWORD_TO_VEHICLE[params.keyword];

		const size = NEAR_ME_KEYWORD_TO_SIZE[params.keyword];

		const hasLatLng = query && query.latitude && query.longitude;

		if (hasLatLng) {
			const searchQuery = {
				latitude: query.latitude,
				longitude: query.longitude,
				subPageType: 'nearMePages',
				...(amenity ? { amenity } : {}),
				...(vehicle ? { vehicle, searchType: 'vehicle' } : {}),
				...(size ? { size } : {}),
				...(params.keyword === 'storage-deals' && !query.dealsFilter
					? { dealsFilter: 'all' }
					: {}),
			};
			const searchParams = pick(searchQuery, allowedSearchParams);
			searchParams.listingsPerPage = 30;

			return dispatch({
				[API]: {
					types: [
						SEARCH.SEARCH_LANDING_REQUEST,
						SEARCH.SEARCH_LANDING_SUCCESS,
						SEARCH.SEARCH_LANDING_FAILURE,
					],
					endpoint: '/search',
					query: searchParams,
					meta: {
						pageType: PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING,
						...(nmpKeyword ? { nmpKeyword } : {}),
						...(nmpSlug ? { nmpSlug } : {}),
					},
					adapter: formatSearchFacilities,
					schema: searchResponseSchema,
				},
			});
		}

		const apiQuery = {
			city,
			state,
			location,
			keyword: params.keyword || '',
			subPageType: 'nearMePages',
			...(amenity ? { amenity } : {}),
			...(vehicle ? { vehicle, searchType: 'vehicle' } : {}),
			...(size ? { size } : {}),
			...(params.keyword === 'storage-deals' && !query.dealsFilter
				? { dealsFilter: 'all' }
				: {}),
			...query,
		};

		return dispatch(
			getSearchLandingPage(apiQuery, {
				pageType: PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING,
				...(nmpKeyword ? { nmpKeyword } : {}),
				...(nmpSlug ? { nmpSlug } : {}),
			})
		);
	};
}

export function getVehicleLandingPage(params, query) {
	return getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			searchType: 'vehicle',
			vehicle: params.vehicle,
			subPageType: PAGE_SUB_TYPES.VEHICLE_LANDING,
			...query,
		},
		{ pageType: PAGE_SUB_TYPES.VEHICLE_LANDING }
	);
}

export function getAmenityLandingPage(params, query) {
	return getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			amenity: params.amenity,
			subPageType: PAGE_SUB_TYPES.AMENITY_LANDING,
			...query,
		},
		{ pageType: PAGE_SUB_TYPES.AMENITY_LANDING }
	);
}

export function getNeighborhoodLandingPage(params, query) {
	return getSearchLandingPage(
		{
			city: params.city,
			state: params.state,
			location: `${params.neighborhood || ''}, ${params.city || ''}, ${
				params.state || ''
			}`,
			neighborhood: params.neighborhood || '',
			subPageType: PAGE_SUB_TYPES.NEIGHBORHOOD_LANDING,
			...query,
		},
		{ pageType: PAGE_SUB_TYPES.NEIGHBORHOOD_LANDING }
	);
}

export function getStorageDealsPage(params) {
	return (dispatch, getState) => {
		const { user, app } = getState();
		const geolocation = get(user, 'geolocation', {});
		const city = geolocation.city || params.city || '';
		const state = geolocation.region || params.state || '';
		const location = [city, state].filter(Boolean).join(', ');
		return dispatch({
			[API]: {
				types: [
					SEARCH.STORAGE_DEALS_REQUEST,
					SEARCH.STORAGE_DEALS_SUCCESS,
					SEARCH.STORAGE_DEALS_FAILURE,
				],
				endpoint: '/page/storageDealsLanding',
				query: {
					searchType: 'storage-only',
					city,
					state,
					location,
					...params,
				},
				meta: {
					page: 'storage',
					baseURL: get(app, 'baseUrl', ''),
				},
				adapter: formatSearchFacilities,
				schema: searchResponseSchema,
			},
		});
	};
}

export function fetchStorageDealsResults(query) {
	return (dispatch, getState) => {
		const { app } = getState();
		return dispatch({
			[API]: {
				types: [
					SEARCH.DEALS_REQUEST,
					SEARCH.DEALS_SUCCESS,
					SEARCH.DEALS_FAILURE,
				],
				endpoint: '/search/storage-deals',
				query: pick(query, allowedSearchParams),
				meta: {
					baseURL: get(app, 'baseUrl', ''),
				},
			},
		});
	};
}

export function fetchSearchResults(query) {
	return (dispatch, getState) => {
		const { app } = getState();
		const params = pick(query, allowedSearchParams);

		params.listingsPerPage = 30;

		return dispatch({
			[API]: {
				types: [SEARCH.REQUEST, SEARCH.SUCCESS, SEARCH.FAILURE],
				endpoint: '/search',
				query: {
					...params,
				},
				meta: {
					baseURL: get(app, 'baseUrl', ''),
				},
			},
		});
	};
}

export function fetchSearchPageResults(query) {
	const params = pick(query, allowedSearchParams);

	params.listingsPerPage = 30;

	return {
		[API]: {
			types: [SEARCH.REQUEST, SEARCH.SUCCESS, SEARCH.FAILURE],
			endpoint: '/page/search',
			query: {
				...params,
			},
		},
	};
}

export function getFiveByFiveLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '5x5',
			subPageType: 'city_5x5_landing_page',
			...query,
		},
		{ pageType: 'city_5x5_landing_page' }
	);

	return response;
}

export function getTenByTenLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x10',
			subPageType: 'city_10x10_landing_page',
			...query,
		},
		{ pageType: 'city_10x10_landing_page' }
	);

	return response;
}

export function getFiveByTenLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '5x10',
			subPageType: 'city_5x10_landing_page',
			...query,
		},
		{ pageType: 'city_5x10_landing_page' }
	);

	return response;
}

export function getFiveByFifteenLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '5x15',
			subPageType: 'city_5x15_landing_page',
			...query,
		},
		{ pageType: 'city_5x15_landing_page' }
	);

	return response;
}

export function getTenByFifteenLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x15',
			subPageType: 'city_10x15_landing_page',
			...query,
		},
		{ pageType: 'city_10x15_landing_page' }
	);

	return response;
}

export function getTenByTwentyLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x20',
			subPageType: 'city_10x20_landing_page',
			...query,
		},
		{ pageType: 'city_10x20_landing_page' }
	);

	return response;
}

export function getTenByTwentyFiveLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x25',
			subPageType: 'city_10x25_landing_page',
			...query,
		},
		{ pageType: 'city_10x25_landing_page' }
	);

	return response;
}

export function getTenByThirtyLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x30',
			subPageType: 'city_10x30_landing_page',
			...query,
		},
		{ pageType: 'city_10x30_landing_page' }
	);

	return response;
}

export function getMiniStorageLandingPage(params, query) {
	const queryParams = {
		...query,
		city: params.city || '',
		state: params.state || '',
		location: `${params.city || ''}, ${params.state || ''}`,
		landingPageSize: 'mini-storage',
		subPageType: 'city_mini_storage_landing_page',
	};

	return getSearchLandingPage(queryParams, {
		pageType: 'city_mini_storage_landing_page',
	});
}

export default searchSlice.reducer;
